/**
 * Allergen aggregation — PURE domain functions.
 *
 * RULE (CLAUDE.md, re-anchored on the naturalesa 2026-07-06, refined 2026-09-07):
 * a matèria primera — and any purchased-only article — declares its 14 EU
 * allergens MANUALLY: nothing upstream can derive them. A MANUFACTURED article
 * (semielaborat / producte acabat) CHOOSES per article
 * (articles.food_safety_mode): 'derived' auto-aggregates them from the BOM up to
 * the recipe / FT / label, 'manual' declares them on the article itself (a
 * supplier-declared composition, a resold good, a legacy sheet). This module
 * consolidates the per-ingredient marks of a BOM into a single set for the
 * produced article, and formats them for the FT and the label.
 *
 * Consolidation rule (regulatory): "contains" dominates "traces". If ANY ingredient
 * *contains* an allergen, the product *contains* it. If no ingredient contains it but at
 * least one carries it as *traces*, the product carries *traces*. Unknown allergens are
 * dropped (only the 14 EU codes are emitted).
 *
 * PURITY: no I/O, no clock, no randomness.
 */

import {
  type AllergenCode,
  type AllergenPresence,
  type ArticleType,
  type FoodSafetyMode,
  ALLERGEN_CODES,
} from '../enums/index.js';
import { EU_ALLERGENS } from '../constants/index.js';

/**
 * Whether the Catàleg fitxa shows the food-safety block (ingredients, allergens, nutrition and notes)
 * for this article (OBR-101). A «producte auxiliar» (`article_type` 'packaging': envasos, etiquetes,
 * material que no arriba a l'aliment) no en té cap, així que el bloc no s'hi ensenya; la resta de
 * tipologies el mantenen.
 *
 * Decideix només la VISIBILITAT: el que un auxiliar ja tingués desat no s'esborra ni es toca, i la
 * regla de derivació (`foodSafetyCanBeDerived`) és una altra cosa.
 */
export function foodSafetyBlockVisible(article: { readonly article_type: ArticleType }): boolean {
  return article.article_type !== 'packaging';
}

/**
 * Whether an article is ALLOWED to derive its food-safety data from a BOM at all
 * (naturalesa rule): only a manufactured article has one. A purchased-only
 * article — a matèria primera, a producte auxiliar, or a RESOLD finished good
 * with no recipe — can only ever declare it manually, so its
 * `food_safety_mode` is pinned to 'manual' (DB CHECK + form gate).
 */
export function foodSafetyCanBeDerived(article: {
  readonly is_manufactured: boolean;
}): boolean {
  return article.is_manufactured;
}

/**
 * Whether THIS article's ingredients / allergens / nutrition are system-derived
 * from the BOM of its active recipe version, rather than declared on the article.
 * Single source of truth for the form gate, the FT generation and the server
 * trigger (enforce_derived_allergens mirrors this exact predicate).
 */
export function foodSafetyIsDerived(article: {
  readonly is_manufactured: boolean;
  readonly food_safety_mode: FoodSafetyMode;
}): boolean {
  return foodSafetyCanBeDerived(article) && article.food_safety_mode === 'derived';
}

/** A single allergen mark on a BOM ingredient article. */
export interface AllergenMark {
  readonly allergen: AllergenCode;
  readonly presence: AllergenPresence;
}

/** One BOM line for aggregation purposes (only the allergen marks matter here). */
export interface BomItemAllergens {
  /** The marked allergens of this ingredient article (manual for MP, derived for semi). */
  readonly allergens: readonly AllergenMark[];
}

/** A consolidated allergen result, ready to snapshot onto recipe/FT/label. */
export interface AggregatedAllergen {
  readonly allergen: AllergenCode;
  readonly presence: AllergenPresence;
}

const EU_ANNEX_INDEX: ReadonlyMap<AllergenCode, number> = new Map(
  EU_ALLERGENS.map((a) => [a.code, a.euAnnexNumber]),
);

const VALID_CODES: ReadonlySet<AllergenCode> = new Set(ALLERGEN_CODES);

/**
 * Aggregate the union of the 14 EU allergens across a BOM.
 *
 * @param bomItems The BOM lines (each carrying its ingredient's allergen marks). May be
 *                 empty (→ empty result). Lines with no allergens contribute nothing.
 * @returns Consolidated allergens, deduplicated, "contains" dominating "traces",
 *          ordered by the official EU Annex II number (stable, deterministic).
 */
export function aggregateAllergens(
  bomItems: readonly BomItemAllergens[],
): AggregatedAllergen[] {
  // Track strongest presence seen per allergen: true = contains, false = traces only.
  const containsSet = new Set<AllergenCode>();
  const tracesSet = new Set<AllergenCode>();

  for (const item of bomItems) {
    for (const mark of item.allergens) {
      // Defensive: ignore anything not one of the 14 EU codes.
      if (!VALID_CODES.has(mark.allergen)) continue;
      if (mark.presence === 'contains') {
        containsSet.add(mark.allergen);
      } else {
        tracesSet.add(mark.allergen);
      }
    }
  }

  const result: AggregatedAllergen[] = [];
  for (const allergen of containsSet) {
    result.push({ allergen, presence: 'contains' });
  }
  for (const allergen of tracesSet) {
    // "contains" dominates: only emit traces if not already a "contains".
    if (!containsSet.has(allergen)) {
      result.push({ allergen, presence: 'traces' });
    }
  }

  // Deterministic order by EU Annex II number (1..14).
  result.sort(
    (a, b) =>
      (EU_ANNEX_INDEX.get(a.allergen) ?? 0) - (EU_ANNEX_INDEX.get(b.allergen) ?? 0),
  );

  return result;
}

/** Localized wording the allergens text needs (resolved by the caller's i18n). */
export interface AllergensTextLabels {
  /** Heading for the «contains» group, e.g. "Conté". */
  readonly contains: string;
  /** Heading for the «traces» group, e.g. "Pot contenir". */
  readonly mayContain: string;
  /** Localized name of one allergen code, e.g. 'gluten' → "Gluten". */
  readonly nameOf: (allergen: AllergenCode) => string;
}

/**
 * The declared allergens as ONE regulatory line, for the FT snapshot and the
 * label: «Conté: gluten, llet. Pot contenir: fruits de closca.». Groups are
 * emitted only when non-empty, so a product with no traces prints just the
 * «Conté» sentence and one with neither returns the empty string.
 *
 * The input order is preserved verbatim — pass the output of
 * {@link aggregateAllergens} to get the EU Annex II order.
 *
 * PURITY: string formatting only; every word comes from `labels`.
 */
export function buildAllergensText(
  allergens: readonly AggregatedAllergen[],
  labels: AllergensTextLabels,
): string {
  const group = (presence: AllergenPresence): string[] =>
    allergens.filter((a) => a.presence === presence).map((a) => labels.nameOf(a.allergen));
  const sentences: string[] = [];
  const contains = group('contains');
  if (contains.length > 0) sentences.push(`${labels.contains}: ${contains.join(', ')}.`);
  const traces = group('traces');
  if (traces.length > 0) sentences.push(`${labels.mayContain}: ${traces.join(', ')}.`);
  return sentences.join(' ');
}
